import type { CustomTypeOptions, ParseKeys } from "i18next"
import type { LucideIcon } from "lucide-react"
import React from "react"

import type { Workspace as HeldWorkspace } from "@/types/serializers"

// A workspace, as the server names it: the kind of principal whose pages it
// holds and that principal's id. Everything else about it, its root and its
// destinations, comes from the owner type's definition.
export type Workspace = Pick<HeldWorkspace, "ownerType" | "ownerId">

export type WorkspaceOwnerType = Workspace["ownerType"]

type Resources = CustomTypeOptions["resources"]

export type WorkspaceNamespace = keyof Resources & string

// A translation key qualified by its namespace, "teams:index.title". The
// namespace is what the reader loads; the key is checked against it.
export type WorkspaceKey = {
  [N in WorkspaceNamespace]: `${N}:${ParseKeys<N>}`
}[WorkspaceNamespace]

export type WorkspaceLink = {
  key: string
  // Null for a destination that has no route yet: the workspace card and the
  // menu entry both render it, disabled.
  href: string | null
  titleKey: WorkspaceKey
  descriptionKey: WorkspaceKey
  icon: LucideIcon
}

export type WorkspaceDestination = Omit<WorkspaceLink, "href"> & {
  href: ((ownerId: string) => string) | null
}

// One owner type's workspace: what the header's menu section, the workspace
// page's cards and every trail's root crumb are built from. Each domain
// defines its own (pages/educators/teacher-workspace.ts and its siblings);
// the composition root gives them all to the shell, which imports none.
export type WorkspaceDefinition = {
  ownerType: WorkspaceOwnerType
  // The workspace's home page, the first crumb of every trail in it.
  root: (ownerId: string) => string
  // In the order the workspace page and the role menu list them.
  destinations: readonly WorkspaceDestination[]
  // The namespaces the destinations' keys are qualified by.
  namespaces: readonly WorkspaceNamespace[]
  // The role's mark in the header: its icon and the tint behind it.
  icon: LucideIcon
  tint: string
}

// The workspace's destinations, in order, each with its title, description
// and icon. The workspace page and the header's role menu both list from
// here so the two never drift.
export function workspaceLinks(
  { destinations }: WorkspaceDefinition,
  ownerId: string,
): WorkspaceLink[] {
  return destinations.map(({ href, ...link }) => ({
    ...link,
    href: href === null ? null : href(ownerId),
  }))
}

// How a page knows the workspace it belongs to: the owner type its domain
// gives it, and the route parameter that holds the owner's id. A page's
// workspace is part of its identity, so it is read from the page's own
// route, never from what the signed-in user holds.
export type WorkspaceOwner = {
  ownerType: WorkspaceOwnerType
  param: string
}

export const WORKSPACE_OWNERS = {
  teacher: { ownerType: "teacher", param: "teacherId" },
  school: { ownerType: "school_admin", param: "schoolId" },
  student: { ownerType: "student", param: "studentId" },
  // The student's own record pages, whose route names the student as :id.
  studentRecord: { ownerType: "student", param: "id" },
} as const satisfies Record<string, WorkspaceOwner>

// The workspace a page's route names. Raises when the route has no such
// parameter: the page is registered under the wrong owner.
export function workspaceFromPathParams(
  { ownerType, param }: WorkspaceOwner,
  pathParams: Record<string, string>,
): Workspace {
  if (!Object.hasOwn(pathParams, param)) {
    throw new Error(`The page's route names no ${param} for its workspace`)
  }
  return { ownerType, ownerId: pathParams[param] }
}

// The definitions, one per owner type, as the composition root provides
// them to everything under it.
export type WorkspaceDefinitions = Readonly<
  Record<WorkspaceOwnerType, WorkspaceDefinition>
>

export const WorkspaceDefinitionsContext =
  React.createContext<WorkspaceDefinitions | null>(null)

// The definition of one owner type's workspace. Raises on an owner type no
// definition was provided for: the server named a workspace the frontend
// does not know.
export function workspaceDefinition(
  definitions: Readonly<Record<string, WorkspaceDefinition>>,
  ownerType: string,
): WorkspaceDefinition {
  if (!Object.hasOwn(definitions, ownerType)) {
    throw new Error(`No workspace definition for the owner type ${ownerType}`)
  }
  return definitions[ownerType]
}

export function useWorkspaceDefinitions(): WorkspaceDefinitions {
  const definitions = React.use(WorkspaceDefinitionsContext)
  if (definitions === null) {
    throw new Error("Rendered outside the workspace definitions' provider")
  }
  return definitions
}

export function useWorkspaceDefinition(ownerType: string): WorkspaceDefinition {
  return workspaceDefinition(useWorkspaceDefinitions(), ownerType)
}
